import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { Button, IconButton } from '../../antd/button.js';
import { useUiDirection } from '../../antd/direction.js';
import { useTooltipSuppression } from '../../antd/overlays.js';
import { useReducedMotion } from '../../motion/internal/env.js';
import { COACHMARK_ROOT_ATTR } from './anchors.js';
import { arrowIntent, isEditable, isQuickSearchShortcut, swallow, trapTab } from './focus-trap.js';
import { DEFAULT_COACHMARK_LABELS } from './labels.js';
import { placeOffer, placePanel } from './position.js';
import { Spotlight } from './spotlight.js';
import { useAnchorRect } from './use-anchor-rect.js';
/**
 * ════════════════════════════════════════════════════════════════════════════
 *  COACHMARK — the tour engine's one component
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Four presentations of one step:
 *
 *   floating  a spotlight on the target and a panel beside it (≥ md). Modal:
 *             `role="dialog" aria-modal="true"`, focus moves to the heading on
 *             every step, Tab cycles inside, Esc closes the tour.
 *   sheet     the same panel docked to the bottom edge (< md), no arrow.
 *   offer     a quiet, NON-modal "want a tour?" bubble rendered in place as
 *             its trigger's next sibling, so Tab from the chip reaches it. No
 *             focus theft, no scrim.
 *   inline    a card in normal document flow inside a Drawer host (the
 *             session form). A body-portaled panel is unreachable inside a
 *             focus-locked Drawer, so the card lives inside the Drawer and
 *             outlines its target instead of spotlighting it.
 *
 * Why not antd `<Tour>`: its panel renderer cannot be replaced (no dialog
 * role, no focus move), its arrow keys are hard-wired LTR, its body portal is
 * unreachable from inside a focus-locked Drawer, and it draws a missing target
 * as a centred card. `docs/DECISIONS.md` has the full comparison.
 *
 * The engine renders; it never decides. Which steps exist, what is recorded
 * and when a tour is offered belong to the panel's runner.
 */
export function Coachmark(props) {
    if (!props.open)
        return null;
    if (props.mode === 'offer')
        return _jsx(OfferCoachmark, { ...props });
    if (props.mode === 'inline')
        return _jsx(InlineCoachmark, { ...props });
    return _jsx(ModalCoachmark, { ...props });
}
function useLabels(labels) {
    return { ...DEFAULT_COACHMARK_LABELS, ...labels };
}
function joinClass(...parts) {
    return parts.filter(Boolean).join(' ');
}
function focusWithoutScroll(el) {
    try {
        el?.focus({ preventScroll: true });
    }
    catch {
        el?.focus();
    }
}
function StepContent({ props, labels, titleId, bodyId, headingRef }) {
    const { step, index, total } = props;
    const last = index >= total - 1;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "bk-coachmark__header", children: [_jsx("span", { className: "bk-coachmark__counter", children: labels.counter(index + 1, total) }), _jsx("span", { className: "bk-coachmark__dots", "aria-hidden": "true", children: Array.from({ length: total }, (_, i) => (_jsx("span", { className: joinClass('bk-coachmark__dot', i === index && 'is-active') }, i))) }), _jsx(IconButton, { className: "bk-coachmark__close", type: "text", size: "small", name: "close", label: labels.close, onClick: () => props.onDismiss('close') })] }), _jsx("h2", { id: titleId, ref: headingRef, tabIndex: -1, className: "bk-coachmark__title", children: step.title }), _jsx("div", { id: bodyId, className: "bk-coachmark__body", children: step.body }), _jsxs("div", { className: "bk-coachmark__actions", children: [!last ? (_jsx(Button, { type: "text", size: "small", className: "bk-coachmark__skip", onClick: () => props.onDismiss('skip'), children: labels.skip })) : null, _jsxs("span", { className: "bk-coachmark__nav", children: [index > 0 ? _jsx(Button, { onClick: props.onPrev, children: labels.prev }) : null, _jsx(Button, { type: "primary", onClick: last ? props.onFinish : props.onNext, children: last ? labels.finish : labels.next })] })] })] }));
}
/* -------------------------------------------------------------------------- */
/* floating + sheet                                                           */
/* -------------------------------------------------------------------------- */
const SHEET_EDGE = 12;
/** How long after mount (or a step change) the live region is filled. */
const LIVE_REGION_DELAY_MS = 100;
function ModalCoachmark(props) {
    const { step, mode, hidden = false, index, total } = props;
    const labels = useLabels(props.labels);
    const dir = useUiDirection();
    const reduced = useReducedMotion();
    const titleId = useId();
    const bodyId = useId();
    const panelRef = useRef(null);
    const headingRef = useRef(null);
    // Tooltips would float over the scrim; the step is modal, so none may open.
    useTooltipSuppression(!hidden);
    const rect = useAnchorRect(step.target, !hidden, props.onAnchorLost);
    const padding = step.spotlightPadding ?? 6;
    // The latest callbacks, read by the one long-lived key listener.
    const latest = useRef(props);
    useLayoutEffect(() => {
        latest.current = props;
    });
    /* ── focus: the heading on every step; back to the launcher at the end ── */
    const returnFocus = useRef(props.returnFocusTo);
    useLayoutEffect(() => {
        returnFocus.current = props.returnFocusTo;
    }, [props.returnFocusTo]);
    useEffect(() => {
        return () => {
            const back = returnFocus.current;
            if (back && back.isConnected)
                focusWithoutScroll(back);
            else
                focusWithoutScroll(document.getElementById('bk-main'));
        };
    }, []);
    useEffect(() => {
        if (hidden)
            return;
        focusWithoutScroll(headingRef.current);
    }, [hidden, index, step.id]);
    /* ── keys: capture phase on window, so an underlying Drawer never sees Esc ── */
    useEffect(() => {
        if (hidden)
            return;
        const onKeyDown = (event) => {
            const panel = panelRef.current;
            if (!panel)
                return;
            const current = latest.current;
            if (event.key === 'Escape') {
                if (event.isComposing)
                    return;
                swallow(event);
                current.onDismiss('escape');
                return;
            }
            if (event.key === 'Tab') {
                trapTab(event, panel);
                return;
            }
            if (isQuickSearchShortcut(event)) {
                swallow(event);
                return;
            }
            const intent = arrowIntent(event.key, dir);
            const active = document.activeElement;
            if (intent && active && panel.contains(active) && !isEditable(active)) {
                event.preventDefault();
                if (intent === 'next') {
                    if (current.index >= current.total - 1)
                        current.onFinish();
                    else
                        current.onNext();
                }
                else if (current.index > 0) {
                    current.onPrev();
                }
            }
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [hidden, dir]);
    /* ── measure the panel, then place it ── */
    const [size, setSize] = useState({ width: 0, height: 0 });
    useLayoutEffect(() => {
        const panel = panelRef.current;
        if (!panel)
            return;
        const measure = () => {
            const next = { width: panel.offsetWidth, height: panel.offsetHeight };
            setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
        };
        measure();
        if (typeof ResizeObserver === 'undefined')
            return;
        const observer = new ResizeObserver(measure);
        observer.observe(panel);
        return () => observer.disconnect();
    }, [index, step.id, mode, hidden]);
    const insets = props.viewportInsets?.() ?? { top: 0, bottom: 0 };
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const padded = rect
        ? {
            top: rect.top - padding,
            left: rect.left - padding,
            width: rect.width + padding * 2,
            height: rect.height + padding * 2,
        }
        : null;
    let placement = null;
    let sheetDockTop = false;
    if (mode === 'floating') {
        placement = placePanel({
            target: step.target ? padded : null,
            panel: size,
            viewport,
            insets,
            placement: step.placement ?? 'auto',
            dir,
        });
    }
    else if (padded) {
        // A target still under the sheet after the runner's scroll (a fixed
        // header control, say) moves the sheet to the top, below the header.
        sheetDockTop = padded.top + padded.height > viewport.height - size.height - SHEET_EDGE * 2;
    }
    const panelStyle = mode === 'floating' && placement
        ? { top: Math.round(placement.top), left: Math.round(placement.left) }
        : sheetDockTop
            ? { insetBlockStart: insets.top + SHEET_EDGE, insetBlockEnd: 'auto' }
            : undefined;
    const arrow = mode === 'floating' && placement?.arrow ? placement : null;
    /* ── the live region: mounted empty, filled a beat later ──
       A region that appears already holding text is not reliably announced,
       so the first step's position would be lost; filling it after mount (and
       on every step change) makes each «مرحلهٔ n از m» a real change. */
    const liveText = hidden ? '' : labels.liveStep(index + 1, total);
    const [live, setLive] = useState('');
    useEffect(() => {
        if (!liveText) {
            setLive('');
            return;
        }
        const timer = window.setTimeout(() => setLive(liveText), LIVE_REGION_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [liveText]);
    const onSentinel = (edge) => () => {
        const panel = panelRef.current;
        if (!panel)
            return;
        const items = [...panel.querySelectorAll('button:not([disabled]), a[href]')];
        focusWithoutScroll(edge === 'start' ? items[items.length - 1] : items[0]);
    };
    if (typeof document === 'undefined')
        return null;
    return createPortal(_jsxs("div", { className: joinClass('bk-coachmark-root', reduced && 'bk-coachmark--reduced', props.className), [COACHMARK_ROOT_ATTR]: '', dir: dir, hidden: hidden, children: [_jsx(Spotlight, { rect: step.target ? rect : null, padding: padding }), _jsx("span", { tabIndex: 0, "data-bk-focus-sentinel": "", className: "bk-coachmark__sentinel", onFocus: onSentinel('start') }), _jsxs("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, "aria-describedby": bodyId, className: joinClass('bk-coachmark', 'bk-coachmark__panel', mode === 'sheet' ? 'bk-coachmark--sheet' : 'bk-coachmark--floating', sheetDockTop && 'bk-coachmark--sheet-top', placement && `bk-coachmark--side-${placement.side}`), style: panelStyle, children: [arrow?.arrow ? (_jsx("span", { className: `bk-coachmark__arrow bk-coachmark__arrow--${arrow.side}`, "aria-hidden": "true", style: arrow.arrow.axis === 'x' ? { left: arrow.arrow.offset } : { top: arrow.arrow.offset } })) : null, _jsx("div", { className: "bk-coachmark__step", children: _jsx(StepContent, { props: props, labels: labels, titleId: titleId, bodyId: bodyId, headingRef: headingRef }) }, `${index}:${step.id}`)] }), _jsx("span", { tabIndex: 0, "data-bk-focus-sentinel": "", className: "bk-coachmark__sentinel", onFocus: onSentinel('end') }), _jsx("div", { className: "bk-coachmark__live", "aria-live": "polite", "aria-atomic": "true", children: live })] }), document.body);
}
/* -------------------------------------------------------------------------- */
/* offer                                                                      */
/* -------------------------------------------------------------------------- */
function OfferCoachmark(props) {
    const { step, hidden = false } = props;
    const reduced = useReducedMotion();
    const dir = useUiDirection();
    const titleId = useId();
    const bodyId = useId();
    const offerRef = useRef(null);
    const [place, setPlace] = useState(null);
    /* ── keep the panel on screen ──
       The stylesheet hangs the offer off its trigger's inline-end edge, which
       in RTL grows it rightwards whatever room is there. While it is
       `position: absolute` (≥ md) it is measured and slid into the viewport;
       below md the stylesheet docks it as a bottom sheet (`position: fixed`)
       and a host may lay it out statically — neither takes an offset. */
    useLayoutEffect(() => {
        const offer = offerRef.current;
        if (!offer || hidden)
            return;
        const measure = () => {
            const anchor = offer.parentElement;
            const positioned = anchor !== null && window.getComputedStyle(offer).position === 'absolute';
            if (!positioned) {
                setPlace(null);
                return;
            }
            const box = anchor.getBoundingClientRect();
            const next = placeOffer({
                anchor: { left: box.left, width: box.width },
                panelWidth: offer.offsetWidth,
                viewportWidth: document.documentElement.clientWidth || window.innerWidth,
                dir,
            });
            setPlace((prev) => prev && Math.round(prev.left) === Math.round(next.left) && Math.round(prev.arrow) === Math.round(next.arrow)
                ? prev
                : next);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [hidden, dir]);
    const onKeyDown = (event) => {
        if (event.key !== 'Escape' || event.nativeEvent.isComposing)
            return;
        // The offer is non-modal and in place: Esc collapses it and goes no
        // further, so a Drawer the chip happens to live in stays open.
        event.stopPropagation();
        props.onDismiss('escape');
    };
    return (_jsxs("div", { id: props.id, role: "dialog", "aria-modal": "false", "aria-labelledby": titleId, "aria-describedby": bodyId, className: joinClass('bk-coachmark', 'bk-coachmark--offer', reduced && 'bk-coachmark--reduced', props.className), hidden: hidden, onKeyDown: onKeyDown, ref: offerRef, style: place ? { left: Math.round(place.left), right: 'auto' } : undefined, children: [_jsx("span", { className: joinClass('bk-coachmark__arrow', 'bk-coachmark__arrow--offer', place && 'is-placed'), "aria-hidden": "true", style: place ? { left: Math.round(place.arrow), right: 'auto' } : undefined }), _jsx("p", { id: titleId, className: "bk-coachmark__title", children: step.title }), _jsx("div", { id: bodyId, className: "bk-coachmark__body", children: step.body }), _jsxs("div", { className: "bk-coachmark__actions bk-coachmark__actions--offer", children: [_jsx(Button, { size: "small", onClick: props.onDecline, children: props.secondaryLabel }), _jsx(Button, { size: "small", type: "primary", onClick: props.onAccept, children: props.primaryLabel })] })] }));
}
/* -------------------------------------------------------------------------- */
/* inline                                                                     */
/* -------------------------------------------------------------------------- */
function InlineCoachmark(props) {
    const { step, hidden = false, index } = props;
    const labels = useLabels(props.labels);
    const dir = useUiDirection();
    const reduced = useReducedMotion();
    const titleId = useId();
    const bodyId = useId();
    const cardRef = useRef(null);
    const headingRef = useRef(null);
    const [slot, setSlot] = useState(null);
    const latest = useRef(props);
    useLayoutEffect(() => {
        latest.current = props;
    });
    // Heading first, so a screen reader hears the step before any button.
    useEffect(() => {
        if (hidden)
            return;
        focusWithoutScroll(headingRef.current);
    }, [hidden, index, step.id]);
    // The target: outlined, scrolled into view inside the drawer's scroller, and
    // followed by a «بازگشت به راهنما» link back up to this card.
    useEffect(() => {
        const target = step.target;
        if (!target || hidden)
            return;
        target.setAttribute('data-bk-coachmark-target', 'true');
        const returnSlot = document.createElement('span');
        returnSlot.setAttribute('data-bk-coachmark-return', '');
        returnSlot.className = 'bk-coachmark__return-slot';
        // A target in a Drawer's header row (its title, its «extra» actions): a
        // block-level link after it would push the header's controls around and
        // grow the row. There the link floats just under the header instead.
        const header = target.closest('.ant-drawer-header');
        if (header) {
            returnSlot.classList.add('bk-coachmark__return-slot--floating');
            header.setAttribute('data-bk-coachmark-return-host', '');
            header.append(returnSlot);
        }
        else {
            target.insertAdjacentElement('afterend', returnSlot);
        }
        setSlot(returnSlot);
        try {
            // `block: 'center'` handles the nested scroller of a Drawer body.
            target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
        }
        catch {
            // Not every environment implements scrolling.
        }
        return () => {
            target.removeAttribute('data-bk-coachmark-target');
            header?.removeAttribute('data-bk-coachmark-return-host');
            returnSlot.remove();
            setSlot((current) => (current === returnSlot ? null : current));
        };
    }, [step.target, hidden, reduced]);
    // Esc belongs to the card ONLY while focus is in it. An Esc anywhere else in
    // the form — an open «خدمت» Select, the patient picker, a date picker —
    // belongs to that control, and the tour neither dismisses nor stops it.
    useEffect(() => {
        if (hidden)
            return;
        const onKeyDown = (event) => {
            const card = cardRef.current;
            const active = document.activeElement;
            if (!card || !active || !card.contains(active))
                return;
            const current = latest.current;
            if (event.key === 'Escape') {
                if (event.isComposing)
                    return;
                swallow(event);
                current.onDismiss('escape');
                return;
            }
            const intent = arrowIntent(event.key, dir);
            if (intent && !isEditable(active)) {
                event.preventDefault();
                if (intent === 'next') {
                    if (current.index >= current.total - 1)
                        current.onFinish();
                    else
                        current.onNext();
                }
                else if (current.index > 0) {
                    current.onPrev();
                }
            }
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [hidden, dir]);
    const backToCard = useCallback(() => {
        const card = cardRef.current;
        try {
            card?.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
        }
        catch {
            // ignore
        }
        focusWithoutScroll(headingRef.current);
    }, [reduced]);
    const returnLink = slot && !hidden
        ? createPortal(_jsx(Button, { type: "link", size: "small", className: "bk-coachmark__return", onClick: backToCard, children: labels.backToGuide }), slot)
        : null;
    return (_jsxs("section", { ref: cardRef, role: "region", "aria-label": props.ariaLabel, className: joinClass('bk-coachmark', 'bk-coachmark--inline', reduced && 'bk-coachmark--reduced', props.className), hidden: hidden, children: [_jsx("div", { className: "bk-coachmark__step", children: _jsx(StepContent, { props: props, labels: labels, titleId: titleId, bodyId: bodyId, headingRef: headingRef }) }, `${index}:${step.id}`), returnLink] }));
}
//# sourceMappingURL=coachmark.js.map